import { defaultFontStyle } from '@proton/components/components/editor/helpers';
import { MESSAGE_ACTIONS } from '@proton/mail-renderer/constants';
import type { MessageStateWithData, PartialMessageState } from '@proton/mail/store/messages/messagesTypes';
import type { MIME_TYPES } from '@proton/shared/lib/constants';
import { setBit } from '@proton/shared/lib/helpers/bitset';
import { canonicalizeInternalEmail } from '@proton/shared/lib/helpers/email';
import type { Address, MailSettings, UserSettings } from '@proton/shared/lib/interfaces';
import type { Recipient } from '@proton/shared/lib/interfaces/Address';
import { MESSAGE_FLAGS } from '@proton/shared/lib/mail/constants';
import {
    DRAFT_ID_PREFIX,
    FW_PREFIX,
    RE_PREFIX,
    formatSubject,
    getOriginalTo,
    isPlainText,
    isSent,
    isSentAndReceived,
} from '@proton/shared/lib/mail/messages';
import generateUID from '@proton/utils/generateUID';
import unique from '@proton/utils/unique';

import { createHTMLDraftContent } from 'proton-mail/helpers/message/draftContent/html';
import { createPlaintextDraftContent } from 'proton-mail/helpers/message/draftContent/plaintext';

import type { DecryptedAttachment } from '../../store/attachments/attachmentsTypes';
import { getFromAddress } from '../addresses';
import { convertToFile } from '../attachment/attachmentConverter';
import { getExpiresIn } from '../expiration';
import { getEmbeddedImages, getRemoteImages, updateImages } from './messageImages';

// Reference: Angular/src/app/message/services/messageBuilder.js

export const CLASSNAME_BLOCKQUOTE = 'protonmail_quote';

/**
 * Copy embedded images from the reference message
 */
export const keepImages = (message: PartialMessageState) => {
    // Embedded images in a loading state can end up blocking the composer.
    // Mitigating this issue by marking loading images as `not-loaded` so the draft code can call `decryptEmbeddedImages`
    // at a later stage. This needs to be reworked by changing the embedding images loading logic so drafts do not stall.
    const embeddedImages = getEmbeddedImages(message).map((image) =>
        image.status === 'loading' ? { ...image, status: 'not-loaded' as const } : image
    );
    const remoteImages = getRemoteImages(message);
    const Attachments = embeddedImages.map((image) => image.attachment);
    const messageImages = updateImages(message.messageImages, undefined, remoteImages, embeddedImages);

    return { Attachments, messageImages };
};

/**
 * Format and build a new message
 * TODO: Define if referenceMessage could ever be defined
 */
const newCopy = (
    {
        data: { Subject = '', ToList = [], CCList = [], BCCList = [] } = {},
        decryption: { decryptedSubject = '' } = {},
    }: PartialMessageState = {},
    useEncrypted = false
): PartialMessageState => {
    return {
        data: { Subject: useEncrypted ? decryptedSubject : Subject, ToList, CCList, BCCList },
    };
};

/**
 * Format and build a reply
 */
export const reply = (referenceMessage: PartialMessageState, useEncrypted = false): PartialMessageState => {
    const Subject = formatSubject(
        useEncrypted ? referenceMessage.decryption?.decryptedSubject : referenceMessage.data?.Subject,
        RE_PREFIX
    );
    const ToList =
        isSent(referenceMessage.data) || isSentAndReceived(referenceMessage.data)
            ? referenceMessage.data?.ToList
            : referenceMessage.data?.ReplyTos;

    const { Attachments, messageImages } = keepImages(referenceMessage);

    return {
        data: { Subject, ToList, Attachments },
        messageImages,
    };
};

/**
 * Format and build a replyAll
 */
export const replyAll = (
    referenceMessage: PartialMessageState,
    useEncrypted = false,
    addresses: Address[]
): PartialMessageState => {
    const { data = {}, decryption: { decryptedSubject = '' } = {} } = referenceMessage;

    const Subject = formatSubject(useEncrypted ? decryptedSubject : data.Subject, RE_PREFIX);

    const { Attachments, messageImages } = keepImages(referenceMessage);

    if (isSent(referenceMessage.data) || isSentAndReceived(referenceMessage.data)) {
        return {
            data: { Subject, ToList: data.ToList, CCList: data.CCList, BCCList: data.BCCList, Attachments },
            messageImages,
        };
    }

    const ToList = data.ReplyTos;

    const CCListAll: Recipient[] = unique([...(data.ToList || []), ...(data.CCList || [])]);

    // Keep other user addresses in the CC list, but remove the address on which the user received the message (using ref message addressID)
    const userAddress = canonicalizeInternalEmail(
        addresses.find((address) => address.ID === data.AddressID)?.Email || ''
    );
    const CCList: Recipient[] = CCListAll.filter(({ Address }) => canonicalizeInternalEmail(Address) !== userAddress);

    return { data: { Subject, ToList, CCList, Attachments }, messageImages };
};

/**
 * Format and build a forward
 */
const forward = (referenceMessage: PartialMessageState, useEncrypted = false): PartialMessageState => {
    const { data, decryption: { decryptedSubject = '' } = {} } = referenceMessage;
    const Subject = formatSubject(useEncrypted ? decryptedSubject : data?.Subject, FW_PREFIX);
    const Attachments = data?.Attachments;

    const { messageImages } = keepImages(referenceMessage);

    return { data: { Subject, ToList: [], Attachments }, messageImages };
};

export const handleActions = (
    action: MESSAGE_ACTIONS,
    referenceMessage: PartialMessageState = {},
    addresses: Address[] = []
): PartialMessageState => {
    // TODO: I would prefere manage a confirm modal from elsewhere
    // const useEncrypted = !!referenceMessage.encryptedSubject && (await promptEncryptedSubject(currentMsg));
    const useEncrypted = !!referenceMessage?.decryption?.decryptedSubject;

    switch (action) {
        case MESSAGE_ACTIONS.REPLY:
            return reply(referenceMessage, useEncrypted);
        case MESSAGE_ACTIONS.REPLY_ALL:
            return replyAll(referenceMessage, useEncrypted, addresses);
        case MESSAGE_ACTIONS.FORWARD:
            return forward(referenceMessage, useEncrypted);
        case MESSAGE_ACTIONS.NEW:
        default:
            return newCopy(referenceMessage, useEncrypted);
    }
};

/**
 * Format a recipient to the string that is displayed at the beginning of blockquotes
 * For HTML content, useHtmlEntities should be true
 */

export const formatRecipientsString = (recipient: Recipient[] = [], format: 'html' | 'plaintext') => {
    return recipient
        .map((recipient) => {
            const name = recipient?.Name || recipient?.Address;
            const address = recipient?.Address;
            const [open, close] = format === 'html' ? ['&lt;', '&gt;'] : ['<', '>'];
            return `${name} ${open}${address}${close}`;
        })
        .join(', ');
};

interface CreateNewDraftParams {
    action: MESSAGE_ACTIONS;
    referenceMessage: PartialMessageState | undefined;
    mailSettings: MailSettings;
    userSettings: UserSettings;
    addresses: Address[];
    getAttachment: (ID: string) => DecryptedAttachment | undefined;
    isOutside?: boolean;
}

export const createNewDraft = ({
    action,
    referenceMessage,
    addresses,
    userSettings,
    mailSettings,
    isOutside,
    getAttachment,
}: CreateNewDraftParams): PartialMessageState => {
    const MIMEType = isOutside
        ? (mailSettings.DraftMIMEType as unknown as MIME_TYPES)
        : referenceMessage?.data?.MIMEType || (mailSettings.DraftMIMEType as unknown as MIME_TYPES);
    const { RightToLeft } = mailSettings;

    let Flags = 0;
    if (mailSettings.AttachPublicKey) {
        Flags = setBit(Flags, MESSAGE_FLAGS.FLAG_PUBLIC_KEY);
    }

    const {
        data: { Subject = '', ToList = [], CCList = [], BCCList = [], Attachments: reusedAttachments = [] } = {},
        messageImages,
    } = handleActions(action, referenceMessage, addresses);

    // If there were some pgp attachments, need to upload them as "initialAttachments"
    const [Attachments, pgpAttachments] = convertToFile(reusedAttachments, getAttachment);

    const originalTo = getOriginalTo(referenceMessage?.data);
    const originalFrom = referenceMessage?.data?.Sender?.Address;
    const originalAddress =
        isSent(referenceMessage?.data) || isSentAndReceived(referenceMessage?.data) ? originalFrom : originalTo;
    const originalAddressID = referenceMessage?.data?.AddressID;
    const initialAttachments = [...(referenceMessage?.draftFlags?.initialAttachments || []), ...pgpAttachments];

    const senderAddress = getFromAddress(addresses, originalAddress, referenceMessage?.data?.AddressID);

    const AddressID = senderAddress?.ID || ''; // Set the AddressID from previous message to convert attachments on reply / replyAll / forward

    // When writing an EO message, we cannot use the Sender which has an external address, so we need to use the Recipient which is a PM address
    const Sender: Recipient = isOutside
        ? {
              Name: referenceMessage?.data?.Sender?.Name || '',
              Address: referenceMessage?.data?.Sender?.Address || '',
          }
        : senderAddress
          ? { Name: senderAddress.DisplayName, Address: senderAddress.Email }
          : { Name: '', Address: '' };

    const ParentID = action === MESSAGE_ACTIONS.NEW ? undefined : referenceMessage?.data?.ID;

    const fontStyle = defaultFontStyle(mailSettings);
    const plain = isPlainText({ MIMEType });

    let expiresIn: Date | undefined;

    if (action === MESSAGE_ACTIONS.REPLY || action === MESSAGE_ACTIONS.REPLY_ALL) {
        // Preset expiration time if it's set on the original message (use the difference between the expiration time and the receive time)
        expiresIn = getExpiresIn(referenceMessage?.data);
    }

    return {
        localID: generateUID(DRAFT_ID_PREFIX),
        data: {
            ToList,
            CCList,
            BCCList,
            Subject,
            PasswordHint: '',
            Attachments,
            MIMEType,
            RightToLeft,
            Flags,
            Sender,
            AddressID,
            Unread: 0,
        },
        messageDocument: {
            initialized: true,
            ...(plain
                ? createPlaintextDraftContent({
                      action,
                      referenceMessage,
                      mailSettings,
                      userSettings,
                      fontStyle,
                      senderAddress,
                  })
                : createHTMLDraftContent({
                      action,
                      referenceMessage,
                      addresses,
                      senderAddress,
                      fontStyle,
                      userSettings,
                      mailSettings,
                  })),
        },
        draftFlags: {
            ParentID,
            action,
            originalTo,
            originalFrom,
            originalAddressID,
            originalMessageFlags: referenceMessage?.data?.Flags,
            initialAttachments,
            expiresIn,
        },
        messageImages,
    };
};

export const cloneDraft = (draft: MessageStateWithData): MessageStateWithData => {
    return {
        ...draft,
        data: { ...draft.data },
        messageDocument: {
            ...draft.messageDocument,
            document: draft.messageDocument?.document?.cloneNode(true) as Element,
        },
    };
};

export const isNewDraft = (localID: string | undefined) => !!localID?.startsWith(DRAFT_ID_PREFIX);
